<script setup>
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import StatsCard from '@/components/ux/StatsCard.vue'

definePage({
  meta: {
    layout: 'default',
    requiresAuth: true,
    roles: ['admin'],
  },
})

const authStore = useAuthStore()
const adminName = computed(() => authStore.currentUser?.name ?? 'Administrator')

const dashboardSections = [
  {
    title: 'Users',
    stats: [
      {
        title: 'Pending Approvals',
        shortTitle: 'Pending',
        value: 7,
        icon: 'ri-user-follow-line',
        chip: '3 today',
      },
      {
        title: 'Registered Anaesthetists',
        shortTitle: 'Anaesthetists',
        value: 28,
        icon: 'ri-heart-pulse-line',
        chip: '3 pending',
      },
      {
        title: 'Registered Secretaries',
        shortTitle: 'Secretaries',
        value: 15,
        icon: 'ri-user-settings-line',
        chip: '1 pending',
      },
      {
        title: 'Free Tier Users',
        shortTitle: 'Free',
        value: 31,
        icon: 'ri-user-line',
        chip: '72% of users',
      },
      {
        title: 'Paid Tier Users',
        shortTitle: 'Paid',
        value: 12,
        icon: 'ri-vip-crown-line',
        chip: '28% of users',
      },
    ],
  },
  {
    title: 'Listings',
    stats: [
      {
        title: 'Total Published Listings',
        shortTitle: 'Total',
        value: 89,
        icon: 'ri-file-list-3-line',
        chip: 'all time',
        wide: true,
      },
      {
        title: 'Draft',
        shortTitle: 'Draft',
        value: 9,
        icon: 'ri-file-edit-line',
        chip: 'unpublished',
      },
      {
        title: 'Publish Pending',
        shortTitle: 'Pub. Pending',
        value: 3,
        icon: 'ri-upload-2-line',
        chip: 'in queue',
      },
      {
        title: 'Live (Open)',
        shortTitle: 'Live',
        value: 14,
        icon: 'ri-broadcast-line',
        chip: '2 new today',
      },
      {
        title: 'Matching',
        shortTitle: 'Matching',
        value: 6,
        icon: 'ri-git-merge-line',
        chip: 'selecting',
      },
      {
        title: 'Assigned',
        shortTitle: 'Assigned',
        value: 18,
        icon: 'ri-user-star-line',
        chip: 'confirmed',
      },
      {
        title: 'Completed',
        shortTitle: 'Completed',
        value: 62,
        icon: 'ri-checkbox-circle-line',
        chip: 'all time',
      },
      {
        title: 'Cancelled',
        shortTitle: 'Cancelled',
        value: 5,
        icon: 'ri-close-circle-line',
        chip: 'this month',
      },
      {
        title: 'Archived',
        shortTitle: 'Archived',
        value: 21,
        icon: 'ri-archive-line',
        chip: 'total',
      },
    ],
  },
  {
    title: 'Rankings',
    stats: [
      {
        title: 'Top Anaesthetist',
        shortTitle: 'Top Ana.',
        value: 'Dr. Bianchi',
        icon: 'ri-award-line',
        chip: '12 ops',
      },
      {
        title: 'Top Secretary',
        shortTitle: 'Top Sec.',
        value: 'Sig.ra Rossi',
        icon: 'ri-trophy-line',
        chip: '8 listings',
      },
      {
        title: 'Most Cancellations',
        shortTitle: 'Cancellations',
        value: 'Dr. Verdi',
        icon: 'ri-alert-line',
        chip: '4 cancels',
      },
    ],
  },
]
</script>

<template>
  <VContainer class="py-4">
    <VCard
      class="mb-6"
      variant="tonal"
    >
      <VCardText>
        <div class="d-flex align-center justify-space-between">
          <div>
            <h2 class="text-h4 mb-2">
              Welcome, {{ adminName }}
            </h2>
            <p class="text-body-2 text-medium-emphasis">
              Review registrations, manage users, and monitor listings.
            </p>
          </div>
          <VIcon
            size="x-large"
            color="primary"
          >
            ri-shield-user-line
          </VIcon>
        </div>
      </VCardText>
    </VCard>

    <div
      v-for="section in dashboardSections"
      :key="section.title"
      class="mb-6"
    >
      <p class="text-overline text-medium-emphasis mb-2 ms-1">
        {{ section.title }}
      </p>
      <VRow dense>
        <VCol
          v-for="stat in section.stats"
          :key="stat.title"
          :cols="stat.wide ? 12 : 6"
          :sm="stat.wide ? 6 : 3"
        >
          <StatsCard
            :title="stat.title"
            :short-title="stat.shortTitle"
            :value="stat.value"
            :icon="stat.icon"
            :chip="stat.chip"
          />
        </VCol>
      </VRow>
    </div>
  </VContainer>
</template>
